/**
 * File: invoiceSendingService.ts
 * Path: apps/admin/src/shared/services/invoiceSendingService.ts
 * Description: How invoices, credit notes and statements leave the business
 * (the invoiceSending callable: email, WhatsApp, download, the statement
 * preview, a PDF exactly as it was sent, today's reminders and Liam's tap on
 * one), and the send log, read straight from invoices/<id>/sends,
 * creditNotes/<id>/sends and statementSends (the server is the only writer).
 */
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import {
  CREDIT_NOTE_COLLECTIONS,
  INVOICE_COLLECTIONS,
  SEND_COLLECTIONS,
  db,
  FunctionsService,
  sortSends,
  toDocumentSend,
  type CustomerStatement,
  type DocumentSend,
  type ReminderKind,
  type ReminderProposal,
  type SendDocument,
  type SendStatus,
} from "@idk/config";

export type SendTarget =
  | { document: "invoice"; invoiceId: string }
  | { document: "creditNote"; creditNoteId: string }
  | { document: "statement"; clientId: string | null; party: string; from: string; to: string };

export interface StatementRange {
  clientId: string | null;
  party: string;
  from: string;
  to: string;
}

export type InvoiceSendingAction =
  | { action: "email"; target: SendTarget; recipient?: string; subject?: string; message?: string }
  | { action: "whatsapp"; target: SendTarget; phone?: string; message?: string }
  | { action: "download"; target: SendTarget }
  | ({ action: "statementPdf" } & StatementRange)
  | { action: "sentPdf"; document: SendDocument; documentId: string; sendId: string }
  | { action: "reminders" }
  | {
      action: "reminderDone";
      invoiceNumber: string;
      kind: ReminderKind;
      outcome: "sent" | "dismissed";
      recipient?: string;
      phone?: string;
      subject?: string;
      message?: string;
    };

export interface InvoiceSendingResult {
  message: string;
  sendIds?: string[];
  status?: SendStatus;
  recipient?: string;
  filename?: string;
  base64?: string;
  whatsappText?: string;
  whatsappHref?: string;
  statement?: CustomerStatement;
  today?: string;
  proposals?: ReminderProposal[];
}

export const invoiceSending = (input: InvoiceSendingAction) =>
  FunctionsService.call<InvoiceSendingAction, InvoiceSendingResult>("invoiceSending", input);

export const SENDS_LIST_LIMIT = 100;

/** The send log of one invoice or credit note, newest first. */
export const listSends = async (document: "invoice" | "creditNote", documentId: string): Promise<DocumentSend[]> => {
  const parent = document === "invoice" ? INVOICE_COLLECTIONS.invoices : CREDIT_NOTE_COLLECTIONS.creditNotes;
  const snap = await getDocs(query(collection(db, parent, documentId, SEND_COLLECTIONS.sends), limit(SENDS_LIST_LIMIT)));
  return sortSends(snap.docs.flatMap((d) => toDocumentSend(d.id, d.data()) ?? []));
};

/** The statements sent to one client, newest first. */
export const listStatementSends = async (clientId: string): Promise<DocumentSend[]> => {
  const snap = await getDocs(
    query(collection(db, SEND_COLLECTIONS.statementSends), where("statement.clientId", "==", clientId), limit(SENDS_LIST_LIMIT)),
  );
  return sortSends(snap.docs.flatMap((d) => toDocumentSend(d.id, d.data()) ?? []));
};
